import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

export default function CountdownForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [sectionId, setSectionId] = useState(38);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Form fields - date, title, description
  const [formData, setFormData] = useState({
    countdown_title: '',
    countdown_description: '',
    countdown_date: ''
  });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    countdown_title: false,
    countdown_description: false,
    countdown_date: false
  });

  // Track original values
  const [originalData, setOriginalData] = useState({...formData});

  const loadCountdownSection = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiBase}/cms/section/38/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const { section, pending_changes } = result.data;
        
        if (section && section.content_published) {
          let published = section.content_published;
          
          if (typeof published === 'string') {
            try {
              published = JSON.parse(published);
            } catch (e) {
              published = {};
            }
          }

          const publishedEn = published.en || {};

          const initialData = {
            countdown_title: publishedEn.countdown_title || '',
            countdown_description: publishedEn.countdown_description || '',
            countdown_date: publishedEn.countdown_date || ''
          };

          setFormData(initialData);
          setOriginalData(initialData);
          setSectionId(section.id);

          // Mark pending fields
          if (Array.isArray(pending_changes)) {
            const pendingMap = {
              countdown_title: false,
              countdown_description: false,
              countdown_date: false
            };

            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                if (change.field_id in pendingMap) {
                  pendingMap[change.field_id] = true;
                }
              }
            });

            setPendingFields(pendingMap);
          }
        }
      }
    } catch (err) {
      console.error('Error loading countdown section:', err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadCountdownSection();
  }, [loadCountdownSection]);

  // Update countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      if (formData.countdown_date) {
        try {
          const targetDate = new Date(formData.countdown_date);
          const now = new Date();
          const difference = targetDate - now;

          if (difference > 0) {
            setTimeRemaining({
              days: Math.floor(difference / (1000 * 60 * 60 * 24)),
              hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
              minutes: Math.floor((difference / 1000 / 60) % 60),
              seconds: Math.floor((difference / 1000) % 60)
            });
          } else {
            setTimeRemaining({
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0
            });
          }
        } catch (e) {
          console.error('Error calculating countdown:', e);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [formData.countdown_date]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Format date to readable format (e.g., "Monday, August 10, 2026")
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString + 'T00:00:00');
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            old_value: originalData[fieldName],
            new_value: formData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        console.log('[CountdownForm] Submit success for field:', fieldName);
        toast.success(`Submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        console.log('[CountdownForm] Submit failed:', result);
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[CountdownForm] Submit error:', err.message);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="hero-form-wrapper">
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="hero-form-wrapper">
        <ToastContainer />

        <div className="hero-form-header">
          <h2 className="hero-form-title">Countdown Section</h2>
          <p className="hero-form-description">Configure countdown timer (all changes require approval)</p>

          <div className="form-section">
            <h3 className="form-section-title">Countdown Configuration</h3>

            <div className="form-field-card">
              <div className="field-header">
                <label>Title</label>
                {pendingFields.countdown_title && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <input
                type="text"
                maxLength="100"
                value={formData.countdown_title}
                onChange={(e) => handleFieldChange('countdown_title', e.target.value)}
                disabled={submitting.countdown_title || pendingFields.countdown_title}
                placeholder="e.g., School Opening Day"
                className="text-input"
              />
              <button
                onClick={() => handleSubmitField('countdown_title')}
                disabled={submitting.countdown_title || pendingFields.countdown_title || formData.countdown_title === originalData.countdown_title}
                className="submit-btn"
              >
                {submitting.countdown_title ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            <div className="form-field-card">
              <div className="field-header">
                <label>Description</label>
                {pendingFields.countdown_description && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <textarea
                maxLength="500"
                value={formData.countdown_description}
                onChange={(e) => handleFieldChange('countdown_description', e.target.value)}
                disabled={submitting.countdown_description || pendingFields.countdown_description}
                placeholder="Description or message for countdown"
                className="textarea-input"
                rows="3"
              />
              <button
                onClick={() => handleSubmitField('countdown_description')}
                disabled={submitting.countdown_description || pendingFields.countdown_description || formData.countdown_description === originalData.countdown_description}
                className="submit-btn"
              >
                {submitting.countdown_description ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            <div className="form-field-card">
              <div className="field-header">
                <label>Target Date</label>
                {pendingFields.countdown_date && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <input
                type="date"
                value={formData.countdown_date}
                onChange={(e) => handleFieldChange('countdown_date', e.target.value)}
                disabled={submitting.countdown_date || pendingFields.countdown_date}
                className="text-input"
              />
              <button
                onClick={() => handleSubmitField('countdown_date')}
                disabled={submitting.countdown_date || pendingFields.countdown_date || formData.countdown_date === originalData.countdown_date}
                className="submit-btn"
              >
                {submitting.countdown_date ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>

          {formData.countdown_date && (
            <div className="form-section">
              <h3 className="form-section-title">Preview</h3>
              
              <div className="form-field-card">
                <p className="countdown-preview-label">Target Date</p>
                <p className="countdown-preview-date">{formatDateDisplay(formData.countdown_date)}</p>
                
                <div className="countdown-preview-grid">
                  <div className="countdown-preview-item">
                    <div className="countdown-preview-value">
                      {String(timeRemaining.days).padStart(2, '0')}
                    </div>
                    <div className="countdown-preview-label-small">Days</div>
                  </div>
                  <div className="countdown-preview-item">
                    <div className="countdown-preview-value">
                      {String(timeRemaining.hours).padStart(2, '0')}
                    </div>
                    <div className="countdown-preview-label-small">Hours</div>
                  </div>
                  <div className="countdown-preview-item">
                    <div className="countdown-preview-value">
                      {String(timeRemaining.minutes).padStart(2, '0')}
                    </div>
                    <div className="countdown-preview-label-small">Minutes</div>
                  </div>
                  <div className="countdown-preview-item">
                    <div className="countdown-preview-value">
                      {String(timeRemaining.seconds).padStart(2, '0')}
                    </div>
                    <div className="countdown-preview-label-small">Seconds</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .countdown-preview-label {
          font-size: 14px;
          color: #666;
          margin-bottom: 10px;
          font-weight: 500;
        }

        .countdown-preview-date {
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin-bottom: 20px;
        }

        .countdown-preview-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .countdown-preview-item {
          text-align: center;
          padding: 15px 12px;
          background-color: #f9f9f9;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
        }

        .countdown-preview-value {
          font-size: 24px;
          font-weight: 700;
          color: #333;
          margin-bottom: 8px;
        }

        .countdown-preview-label-small {
          font-size: 12px;
          color: #999;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .countdown-preview-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </>
  );
}
